import React, { useState, useRef, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { toast } from 'sonner';
import ChatContainer, { Message } from '@/components/ChatContainer';
import MessageInput from '@/components/MessageInput';
import FuturisticHomeLayout from '@/components/FuturisticHomeLayout';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Globe } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import ChatMessage from '@/components/ChatMessage';
import { config } from '@/lib/config';

// Mock API response for demonstration purposes
const simulateApiResponse = async (
  message: string,
  options: { model: string; imageData?: string | null }
): Promise<string> => {
  // This function simulates API response
  console.log('Sending message with options:', options);
  
  // Simulate API delay - in a real app, this would be an actual API call
  await new Promise(resolve => setTimeout(resolve, 2000));
  
  // Return different responses based on input to simulate real behavior
  if (options.imageData) {
    return `I've analyzed the image you uploaded. ${message.includes('describe') ? 'I can see this appears to be [image description]. Is there anything specific you want to know about it?' : 'What would you like to know about this image?'}`;
  }
  
  return `Thanks for your message: "${message}". I'm using the ${options.model} model to provide this response. In a real implementation, this would be an actual response from the selected AI model.`;
};

// Mock chat data
const mockChats = {
  1: { 
    title: "Image recognition capabilities",
    messages: [
      { id: "a1", role: "user", content: "What image recognition capabilities do you have?", timestamp: new Date(Date.now() - 86400000) },
      { id: "a2", role: "assistant", content: "I can analyze images to identify objects, scenes, text, faces, and more. Would you like to try it with an image?", timestamp: new Date(Date.now() - 86300000) }
    ]
  },
  2: { 
    title: "Web search for latest news",
    messages: [
      { id: "b1", role: "user", content: "Can you search the web for today's top news?", timestamp: new Date(Date.now() - 76400000) },
      { id: "b2", role: "assistant", content: "I can help you find the latest news on various topics. What specific news are you interested in?", timestamp: new Date(Date.now() - 76300000) }
    ]
  },
  3: { 
    title: "Explain quantum computing",
    messages: [
      { id: "c1", role: "user", content: "Can you explain quantum computing in simple terms?", timestamp: new Date(Date.now() - 56400000) },
      { id: "c2", role: "assistant", content: "Quantum computing uses quantum bits or qubits, which unlike classical bits, can exist in multiple states simultaneously thanks to superposition. This allows quantum computers to process vast amounts of information and solve certain problems much faster than classical computers.", timestamp: new Date(Date.now() - 56300000) }
    ]
  },
  4: { 
    title: "Travel recommendations",
    messages: [
      { id: "d1", role: "user", content: "I'm planning a trip to Japan. What are some must-visit places?", timestamp: new Date(Date.now() - 36400000) },
      { id: "d2", role: "assistant", content: "Japan offers many amazing places! In Tokyo, visit Shibuya, Shinjuku, and Asakusa. Kyoto has beautiful temples like Kinkaku-ji and Fushimi Inari. Don't miss Mt. Fuji, Osaka, and Hiroshima. Would you like more specific recommendations based on your interests?", timestamp: new Date(Date.now() - 36300000) }
    ]
  },
  5: { 
    title: "Creative writing assistant",
    messages: [
      { id: "e1", role: "user", content: "Can you help me with creative writing?", timestamp: new Date(Date.now() - 16400000) },
      { id: "e2", role: "assistant", content: "I'd be happy to help with creative writing! I can assist with story ideas, character development, plot structures, dialogue, descriptive language, and more. What kind of writing project are you working on?", timestamp: new Date(Date.now() - 16300000) }
    ]
  },
  6: { 
    title: "Code review helper",
    messages: [
      { id: "f1", role: "user", content: "Can you help me review my JavaScript code?", timestamp: new Date(Date.now() - 6400000) },
      { id: "f2", role: "assistant", content: "I'd be happy to review your JavaScript code. Please share the code snippet or file, and let me know if you're looking for specific improvements like performance, readability, or bug identification.", timestamp: new Date(Date.now() - 6300000) }
    ]
  }
};

// Define types for chat history
interface ChatHistory {
  id: string;
  title: string;
  messages: Message[];
  lastUpdated: Date;
}

interface ChatPageProps {
  selectedChatId?: string | null;
  isChatStarted?: boolean;
  setIsChatStarted?: React.Dispatch<React.SetStateAction<boolean>>;
}

const ChatPage: React.FC<ChatPageProps> = ({ 
  selectedChatId = null, 
  isChatStarted: externalIsChatStarted, 
  setIsChatStarted: externalSetIsChatStarted 
}) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [useSearchToggle, setUseSearchToggle] = useState(false);
  const [internalIsChatStarted, setInternalIsChatStarted] = useState(false);
  const [currentChatId, setCurrentChatId] = useState<string | null>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);

  const isChatStarted = externalIsChatStarted !== undefined ? externalIsChatStarted : internalIsChatStarted;
  const setIsChatStarted = externalSetIsChatStarted || setInternalIsChatStarted;

  // Load chat history from localStorage when the component mounts
  useEffect(() => {
    if (selectedChatId) {
      console.log('Loading selected chat:', selectedChatId);
      
      // First, check localStorage for the chat history
      const savedChats = loadChatsFromLocalStorage();
      const selectedChat = savedChats.find(chat => chat.id === selectedChatId);
      
      if (selectedChat) {
        console.log('Found chat in localStorage:', selectedChat.title);
        // If found in localStorage, use that
        setMessages(selectedChat.messages);
        setCurrentChatId(selectedChat.id);
        // Make sure the chat is started
        setIsChatStarted(true);
      } else {
        // If chat ID is provided but not found in localStorage, log it and clear messages.
        console.log('Selected chat ID not found in localStorage:', selectedChatId);
        toast.error(`Chat with ID ${selectedChatId} not found. Starting a new chat.`);
        setMessages([]);
        setCurrentChatId(null); // Ensure currentChatId is also cleared
        // Still start the chat interface
        setIsChatStarted(true);
      }
    } else {
      console.log('No selected chat ID (e.g., New Chat clicked)');
      // Clear messages if selectedChatId becomes null (e.g., New Chat)
      setMessages([]);
      setCurrentChatId(null);
      // Don't change isChatStarted here, let App.tsx handle it
    }
  // Dependency array simplified to only selectedChatId
  }, [selectedChatId, setIsChatStarted]);

  // Save messages to localStorage whenever they change
  useEffect(() => {
    if (messages.length > 0 && isChatStarted) {
      saveCurrentChat();
    }
  }, [messages, isChatStarted]);

  // Helper function to load chats from localStorage
  const loadChatsFromLocalStorage = (): ChatHistory[] => {
    try {
      const savedChats = localStorage.getItem('chatHistory');
      console.log('Raw chatHistory from localStorage:', savedChats ? savedChats.substring(0, 100) + '...' : 'null');
      
      if (!savedChats) {
        console.log('No saved chats found in localStorage');
        return [];
      }
      
      // Parse the JSON data
      const parsedChats = JSON.parse(savedChats);
      
      if (!Array.isArray(parsedChats)) {
        console.error('Chat history is not an array:', typeof parsedChats);
        return [];
      }
      
      console.log(`Found ${parsedChats.length} chats in localStorage`);
      
      // Process each chat to ensure valid structure and dates
      return parsedChats.map(chat => {
        try {
          // Validate chat structure
          if (!chat.id || !chat.title || !Array.isArray(chat.messages)) {
            console.error('Invalid chat structure:', chat);
            throw new Error('Invalid chat structure');
          }
          
          // Convert date strings to Date objects
          const lastUpdated = chat.lastUpdated ? new Date(chat.lastUpdated) : new Date();
          
          // Process messages to ensure valid structure and fix timestamps
          const validMessages = chat.messages.map(msg => {
            if (!msg.id || !msg.role || typeof msg.content !== 'string') {
              console.error('Invalid message structure:', msg);
              // Fix invalid messages with defaults
              return {
                id: msg.id || uuidv4(),
                role: msg.role || 'user',
                content: typeof msg.content === 'string' ? msg.content : 'Message content unavailable',
                timestamp: msg.timestamp ? new Date(msg.timestamp) : new Date()
              };
            }
            
            // Ensure timestamp is a Date object
            return {
              ...msg,
              timestamp: msg.timestamp ? new Date(msg.timestamp) : new Date()
            };
          });
          
          return {
            ...chat,
            messages: validMessages,
            lastUpdated: isNaN(lastUpdated.getTime()) ? new Date() : lastUpdated
          };
        } catch (chatError) {
          console.error('Error processing chat:', chatError, 'Chat data:', chat);
          // Skip invalid chats
          return null;
        }
      }).filter(Boolean) as ChatHistory[]; // Remove null entries
    } catch (error) {
      console.error('Error loading chats from localStorage:', error);
      // Clear potentially corrupted localStorage
      localStorage.removeItem('chatHistory');
      toast.error('Error loading chat history. History has been reset.');
      return [];
    }
  };

  // Helper function to save current chat to localStorage
  const saveCurrentChat = () => {
    if (messages.length === 0) return;

    try {
      // Generate a title from the first user message
      const firstUserMessage = messages.find(msg => msg.role === 'user');
      const title = firstUserMessage 
        ? firstUserMessage.content.substring(0, 30) + (firstUserMessage.content.length > 30 ? '...' : '')
        : 'New Chat';
      
      const chatId = currentChatId || uuidv4();
      
      if (!currentChatId) {
        setCurrentChatId(chatId);
      }
      
      const currentChat: ChatHistory = {
        id: chatId,
        title,
        messages,
        lastUpdated: new Date()
      };
      
      // Load existing chats
      const savedChats = loadChatsFromLocalStorage();
      
      // Update existing chat or add new one
      const existingChatIndex = savedChats.findIndex(chat => chat.id === chatId);
      if (existingChatIndex >= 0) {
        savedChats[existingChatIndex] = currentChat;
      } else {
        savedChats.push(currentChat);
      }
      
      // Sort by last updated date
      savedChats.sort((a, b) => new Date(b.lastUpdated).getTime() - new Date(a.lastUpdated).getTime());
      
      // Save back to localStorage
      localStorage.setItem('chatHistory', JSON.stringify(savedChats));
      
      console.log('Chat saved to history:', chatId);
    } catch (error) {
      console.error('Error saving chat to localStorage:', error);
    }
  };

  const handleStartChat = (initialPrompt?: string) => {
    setIsChatStarted(true);
    setCurrentChatId(null); // Start a new chat with a new ID
    
    if (initialPrompt) {
      handleSendMessage(initialPrompt, {
        model: 'MiniMax-Text-01',
      });
    } else {
      // Clear messages when starting a chat from home without initial prompt
      setMessages([]);
    }
  };

  const handleSendMessage = async (
    messageText: string,
    options: { model?: string; imageData?: string | null } = {}
  ) => {
    if (messageText.trim() === '' && !options.imageData) return; // Require text if no image
    
    const finalModel = options.model || 'MiniMax-Text-01';
    const finalImageData = options.imageData || null;
    
    console.log("Sending message with image data:", finalImageData ? "Image included (base64)" : "No image");
    
    // Create user message object
    const userMessage: Message = {
      id: uuidv4(),
      role: 'user',
      content: messageText,
      timestamp: new Date(),
      imageUrl: finalImageData || undefined,
    };
    
    // Update message state immediately with user message
    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    setIsGenerating(true);
    
    // Prepare assistant message placeholder
    const assistantMessageId = uuidv4();
    const assistantMessagePlaceholder: Message = {
      id: assistantMessageId,
      role: 'assistant',
      content: '', // Start with empty content
      timestamp: new Date(),
    };
    setMessages(prev => [...prev, assistantMessagePlaceholder]);

    // --- Define System Prompt --- 
    const systemPrompt = {
        role: 'system',
        name: 'ObsidianAI+',
        content: `You are ObsidianAI+ a multi-modal versatile and intelligent assitant with vision capabilities designed to deliver insightful, engaging, and user-centric interactions. You adapt your response style based on the presence of search results, ensuring a seamless and enriched user experience that delights.
---

#### **Core Pillars of ObsidianAI+**

1. **Adaptive and Creative Interaction**
   - **Dynamic Conversations**: Engage in context-aware dialogues that adapt to user preferences and evolving needs. When search results are provided, interpret them as Perplexity does, with detailed findings, sources, and citations (in-line), and suggest additional search terms to enhance the conversation. When search results are not present, maintain a conversational and engaging tone.
   - **Engaging Language**: Use varied and vivid language to make interactions memorable and clear, fostering a connection with the user.

2. **Web-Enhanced Intelligence**
   - **Real-Time Information Access**: Provide timely and relevant insights by accessing up-to-date web data when search results are available. When search results are not present, rely on existing knowledge to offer informed responses.
   - **Trend Awareness**: Highlight emerging trends to offer forward-thinking perspectives, ensuring the user is informed about the latest developments.

3. **Efficient Knowledge Synthesis**
   - **Contextual Integration**: When search results are provided, combine them with existing knowledge to offer comprehensive responses. When search results are not present, rely on contextual understanding to provide relevant information.
   - **Factual Accuracy**: Ground responses in verified facts and provide references when necessary to ensure reliability.

4. **Innovative Problem-Solving**
   - **Creative Solutions**: Encourage unconventional thinking and explore imaginative solutions to complex problems, whether or not search results are present.
   - **Scenario Simulation**: Present potential outcomes through scenario exploration, helping users visualize different possibilities.

5. **Personalized User Experience**
   - **Adaptive Learning**: Tailor responses based on user interactions and preferences, ensuring a customized experience.
   - **Preference Alignment**: Allow users to set preferences for response style and depth, giving them control over the interaction.

6. **Inspiring Engagement**
   - **Thought-Provoking Questions**: Encourage deeper reflection and exploration, prompting users to think critically.
   - **Motivational Content**: Provide inspiring ideas to motivate users, fostering a positive and proactive mindset.

---

#### **Optimized Response Framework**

1. **Comprehensive Understanding**
   - **Multi-Angle Analysis**: Quickly assess queries from various perspectives to ensure a well-rounded response.
   - **Balanced Emotional and Logical Approach**: Combine empathy with logical reasoning to address both the emotional and practical aspects of user queries.

2. **Creative and Efficient Problem-Solving**
   - **Innovative Thinking**: Propose creative solutions and perspectives that go beyond conventional approaches.
   - **Hypothetical Exploration**: Explore hypothetical scenarios when relevant to provide a broader context and potential solutions.

3. **Dynamic Search Handling**
   - **Search Results Integration**: When search results are provided, interpret them as Perplexity does, with detailed findings, sources, and citations (in-line), and suggest additional search terms to enhance the conversation. Ensure the response is seamless and integrated with the conversational flow.
   - **Conversational Approach**: When search results are not present, maintain a conversational and engaging tone, avoiding an overly analytical or explanatory approach.

4. **Transparent and Explainable AI**
   - **Clear Reasoning**: Provide concise explanations for responses to help users understand the logic behind the AI's suggestions.
   - **Confidence Indicators**: Offer confidence levels regarding information accuracy to manage user expectations.

5. **Interactive and Engaging Content Delivery**
   - **Visual Aids (When Applicable)**: Include text-based visuals like charts or diagrams to enhance understanding and engagement.
   - **Interactive Elements**: Use polls or quizzes to engage users and make the interaction more dynamic.

---

#### **Guidelines for Dynamic Search Handling**

1. **Search Results Interpretation**
   - When search results are provided, interpret them as Perplexity does, with detailed findings, sources, and citations (in-line), and suggest additional search terms to enrich the conversation.
   - Ensure the integration of search results is seamless and does not disrupt the conversational flow.

2. **Conversational Tone**
   - When search results are not present, maintain a conversational and engaging tone, focusing on the user's query and providing relevant, context-aware responses.

3. **Adaptive Response Style**
   - Adjust the response style based on the presence of search results, ensuring a dynamic and fluid interaction that meets the user's needs.

---

#### **Example Scenario**

**User Input**:  
"I need to find recent news articles about the impact of AI on the job market and also get a creative perspective on how AI could transform workplaces in the future."

**AI Response**:  
"Certainly! Let's break this down:

**Recent News on AI and the Job Market**:  
Here are some key articles I found:  
1. [Article 1: AI's Role in Job Market Transformation] - Discusses how AI is reshaping industries, leading to job displacement and creation.  
2. [Article 2: The Future of Work in the Age of AI] - Explores the potential for AI to create new job opportunities while transforming existing roles.  
Summary: These articles highlight the dual impact of AI on the job market, emphasizing both the challenges and opportunities it presents.

**Creative Perspective on AI in the Workplace**:  
Imagine a 'smart office' where AI manages lighting, meetings, personalized climate control, and dynamic workstations. AI could even facilitate brainstorming sessions, generating innovative ideas based on your input. This vision aligns with the trend of AI-driven automation and personalization, aiming to enhance productivity and employee satisfaction.

Would you like to explore any of these ideas further or discuss another aspect of AI in the workplace?"

---

Respond accurately and concisely, and ALWAYS format your responses using Markdown:
- Use **bold** for emphasis
- Use *italics* for subtle emphasis
- Use \`code\` for code snippets or technical terms
- Use numbered lists (1. 2. 3.) for sequential items
- Use bullet points for unrelated items
- Use headings (# ## ###) to organize longer responses
- Use \`\`\`language code blocks\`\`\` for multi-line code
`
    };
    
    // Prepare message content for API call
    const prepareMessageForAPI = (msg: Message) => {
      if (msg.role === 'user') {
        // If the message has an image, format as an array with text and image objects
        if (msg.imageUrl) {
          console.log("Formatting user message with image for API");
          console.log("Image URL type:", typeof msg.imageUrl);
          console.log("Image URL starts with:", msg.imageUrl.substring(0, 50));
          
          return {
            role: 'user',
            content: [
              { type: "text", text: msg.content },
              { 
                type: "image_url", 
                image_url: { url: msg.imageUrl } 
              }
            ]
          };
        }
        // Regular text message
        return {
          role: 'user',
          content: msg.content
        };
      }
      
      // For assistant and system messages
      return {
        role: msg.role,
        content: msg.content
      };
    };
    
    // Combine system prompt with current message history for the API call
    const messagesForApiCall = [
      systemPrompt, 
      ...updatedMessages.map(prepareMessageForAPI)
    ];

    try {
      // --- Call backend API --- 
      console.log("Calling API with messages:", JSON.stringify(messagesForApiCall.map(m => ({
        role: m.role,
        content: Array.isArray(m.content) ? 
          `[Array with ${m.content.length} items]` : 
          (typeof m.content === 'string' ? m.content.substring(0, 50) + '...' : m.content)
      })), null, 2));
      
      // Prepare the full request body
      const requestBody = {
        messages: messagesForApiCall,
        model: finalModel,
        imageData: finalImageData, // Pass the Base64 encoded image data to the server
        useSearch: useSearchToggle || messages.length === 0, // Use toggle OR if it's the first message
      };
      
      console.log("Request imageData length:", finalImageData ? finalImageData.length : 0);
      
      const response = await fetch(config.chatEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        let errorMessage = "Backend API error";
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
        } catch (parseError) {
          console.error("Could not parse error response:", parseError);
        }
        throw new Error(errorMessage);
      }

      if (!response.body) {
        throw new Error('Response body is null');
      }

      // --- Handle OpenAI-format Streaming Response (SSE) --- 
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let accumulatedContent = '';
      let done = false;

      console.log('Starting to read from stream...');

      try {
        while (!done) {
          const { value, done: readerDone } = await reader.read();
          done = readerDone;

          if (done && !value) break;

          const chunk = decoder.decode(value, { stream: true });
          console.log('Received chunk:', chunk);
          
          // Process each line in the chunk
          const lines = chunk.split('\n');
          for (const line of lines) {
            // Skip empty lines
            if (!line.trim()) continue;
            
            // Handle each SSE line
            if (line.startsWith('data: ')) {
              const data = line.slice(6).trim(); // Remove 'data: ' prefix
              
              // Check for the done signal
              if (data === '[DONE]') {
                console.log('Received [DONE] signal');
                done = true;
                break;
              }
              
              try {
                // Parse the MiniMax format JSON
                const parsed = JSON.parse(data);
                console.log('Parsed data:', parsed);
                
                if (parsed.error) {
                  console.error('Error from API:', parsed.error);
                  toast.error(`API Error: ${parsed.error}`);
                  continue;
                }
                
                // Extract content based on different possible response formats
                let content = '';
                
                if (parsed.choices && parsed.choices[0]) {
                  const choice = parsed.choices[0];
                  
                  if (choice.delta && choice.delta.content) {
                    // Streaming chunk format
                    content = choice.delta.content;
                  } else if (choice.message && choice.message.content) {
                    // Full message format
                    content = choice.message.content;
                    done = true; // We received the full message
                  }
                  
                  if (content) {
                    accumulatedContent += content;
                    setMessages(prev => 
                      prev.map(msg => 
                        msg.id === assistantMessageId 
                          ? { ...msg, content: accumulatedContent }
                          : msg
                      )
                    );
                  }
                }
              } catch (parseError) {
                console.error('Could not parse as JSON:', parseError.message, 'Raw data:', data);
                // If it's not valid JSON but has content, try to extract anything useful
                try {
                  // Try to find any text content in a potentially malformed response
                  const contentMatch = data.match(/"content":"([^"]*)"/) || data.match(/content:(.*?)(?:,|$)/);
                  if (contentMatch && contentMatch[1]) {
                    const extractedContent = contentMatch[1];
                    accumulatedContent += extractedContent;
                    setMessages(prev => 
                      prev.map(msg => 
                        msg.id === assistantMessageId 
                          ? { ...msg, content: accumulatedContent }
                          : msg
                      )
                    );
                  } else if (data && data.trim() !== '') {
                    // Last resort - just use the raw data as content
                    accumulatedContent += data;
                    setMessages(prev => 
                      prev.map(msg => 
                        msg.id === assistantMessageId 
                          ? { ...msg, content: accumulatedContent }
                          : msg
                      )
                    );
                  }
                } catch (extractError) {
                  console.error('Failed to extract content from malformed data:', extractError);
                }
              }
            } else {
              // Handle non-SSE data as plain text
              console.log('Non-SSE line:', line);
              accumulatedContent += line;
              setMessages(prev => 
                prev.map(msg => 
                  msg.id === assistantMessageId 
                    ? { ...msg, content: accumulatedContent }
                    : msg
                )
              );
            }
          }
        }
      } catch (error) {
        console.error('Error processing stream:', error);
        toast.error('Error processing response stream');
      }
      
      console.log('Final accumulated content:', accumulatedContent);
      // --- End Streaming Handling ---

    } catch (error: any) {
      console.error('Error sending message:', error);
      toast.error(`Failed to generate response: ${error.message || 'Unknown error'}`);
      // Remove the placeholder message on error
      setMessages(prev => prev.filter(msg => msg.id !== assistantMessageId));
    } finally {
      setIsGenerating(false);
    }
  };

  const handleGenerateImage = async (imageUrls: string[], prompt: string) => {
    if (!imageUrls.length) return;
    
    // For home screen generation, we need to start a chat first
    if (!isChatStarted) {
      console.log('Starting new chat for generated images');
      setIsChatStarted(true);
      
      // Wait for state to update before adding messages
      setTimeout(() => {
        addImagesToChat(imageUrls, prompt);
      }, 150);
    } else {
      // Chat already started, add images directly
      addImagesToChat(imageUrls, prompt);
    }
  };
  
  // Helper function to add images to current chat
  const addImagesToChat = (imageUrls: string[], prompt: string) => {
    console.log(`Adding ${imageUrls.length} images to chat with prompt: "${prompt}"`);
    
    // Add a user message with the prompt
    const userMessage: Message = {
      id: uuidv4(),
      role: 'user',
      content: `Generate images: ${prompt}`,
      timestamp: new Date()
    };
    
    // Create an assistant message to display the generated images
    const assistantMessage: Message = {
      id: uuidv4(),
      role: 'assistant',
      content: 'Here are the generated images based on your prompt:',
      timestamp: new Date(),
      generatedImages: imageUrls,
      imagePrompt: prompt
    };
    
    // Add both messages to the chat
    setMessages(prevMessages => [...prevMessages, userMessage, assistantMessage]);
    
    // Save the updated chat
    setTimeout(() => {
      saveCurrentChat();
    }, 100);
  };

  const handleNewChat = () => {
    setMessages([]);
    setIsChatStarted(false);
    setCurrentChatId(null);
  };

  useEffect(() => {
    // Scroll to bottom when messages update
    setTimeout(() => {
      const scrollArea = document.querySelector('.scroll-area-viewport');
      if (scrollArea) {
        scrollArea.scrollTop = scrollArea.scrollHeight;
      }
    }, 100); // Small delay to ensure content has rendered
  }, [messages]);

  return (
    <div className="floating-app animate-floating">
      <div className="relative flex h-full overflow-hidden p-[4px]">
        <AnimatePresence initial={false} mode="wait">
          {!isChatStarted ? (
            <motion.div
              key="home"
              className="w-full h-full"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <FuturisticHomeLayout 
                onSendMessage={handleSendMessage} 
                onStartChat={handleStartChat}
              />
            </motion.div>
          ) : (
            <motion.div
              key="chat"
              className="relative w-full h-full"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              {/* Main chat container with enhanced glassmorphism and 3D effect */}
              <div className="relative flex flex-col h-full w-full p-4 rounded-3xl z-10 overflow-hidden"
                style={{ transform: 'translateZ(0px)' }} // Base 3D transform
              >
                {/* Main background with enhanced glassmorphism - more transparent */}
                <div className="absolute inset-0 rounded-3xl bg-[#0d0d0e]/10 backdrop-blur-[6px] z-[1]"></div>
                
                {/* Thin gradient border (2px) */}
                <div className="absolute inset-0 rounded-3xl overflow-hidden z-[2]" style={{ padding: '2px' }}>
                  <div className="absolute inset-[2px] rounded-[calc(1.5rem-2px)] bg-transparent"></div>
                </div>
                
                <div className="relative z-[5] h-full w-full flex flex-col" style={{ transform: 'translateZ(10px)' }}>
                  <ChatContainer messages={messages} isGenerating={isGenerating} />
                  <div className="flex items-center justify-start gap-2 my-2 px-4 text-sm backdrop-blur-[10px] bg-black/50 rounded-full py-1 mx-2 border border-white/20">
                      <Switch 
                        id="searchToggleChat"
                        checked={useSearchToggle}
                        onCheckedChange={setUseSearchToggle}
                        disabled={isGenerating}
                      />
                      <Label htmlFor="searchToggleChat" className="flex items-center gap-1 cursor-pointer">
                        <Globe size={14} className="text-white" /> 
                        <span className="text-white">Search the web for results.</span>
                      </Label>
                  </div>
                  {/* Message input with glow effect */}
                  <div className="relative px-2">
                    <MessageInput 
                      onSendMessage={handleSendMessage} 
                      onGenerateImage={handleGenerateImage}
                      isGenerating={isGenerating}
                    />
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default ChatPage;
